import * as React from "react";
import {
    Typography,
    Box,
    Button as MUIButton,
    Paper,
    Modal,
    Backdrop,
    Fade,
    CircularProgress,
} from "@mui/material";
import { connect } from "react-redux";
import {
    MainInput,
    main_button,
} from "../../app/components/Styles";
import { getServiceFeeTiers, addServiceFeeTier, getVendors, editServiceFeeTier, deleteServiceFeeTier } from "../../app/store/actions/whFinanceActions";
import { useEffect } from "react";
import { notification } from "antd";
import { FoDialog, FoTable, formatEgp } from "./financeUi";
import { os, useFinanceToken } from "./osClient";
import { describeValue, formatStamp } from "./osRules";

export const ServiceFeeTiers = ({
    getServiceFeeTiers,
    addServiceFeeTier,
    getVendors,
    editServiceFeeTier,
    deleteServiceFeeTier,
    tiers,
    vendors,
    isLoading
}) => {
    const [createModalOpen, setCreateModalOpen] = React.useState(false);
    const [creating, setCreating] = React.useState(false);
    const [formData, setFormData] = React.useState({
        name: "",
        minOrders: "",
        maxOrders: "",
        chainzFee: "",
        shippingMultiplier: ""
    });

    const [editModalOpen, setEditModalOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [editingTier, setEditingTier] = React.useState(null);
    const [editFormData, setEditFormData] = React.useState({
        name: "",
        minOrders: "",
        maxOrders: "",
        chainzFee: "",
        shippingMultiplier: ""
    });

    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [tierToDelete, setTierToDelete] = React.useState(null);
    const [deleting, setDeleting] = React.useState(false);
    const token = useFinanceToken();
    const [osTiers, setOsTiers] = React.useState([]);
    const [series, setSeries] = React.useState([]);
    const [history, setHistory] = React.useState(null);
    const [historyRows, setHistoryRows] = React.useState([]);

    const loadOs = React.useCallback(() => {
        if (!token) return;
        os.tiers(token, { includeArchived: true })
            .then((data) => setOsTiers(Array.isArray(data) ? data : (data?.tiers || data?.docs || [])))
            .catch(() => setOsTiers([]));
        os.tierRevenue(token, 6)
            .then((data) => setSeries(data?.series || []))
            .catch(() => setSeries([]));
    }, [token]);

    useEffect(() => {
        getServiceFeeTiers();
        getVendors();
    }, [getServiceFeeTiers, getVendors]);

    useEffect(() => {
        loadOs();
    }, [loadOs]);

    const handleOpenCreateModal = () => {
        setCreateModalOpen(true);
        setFormData({
            name: "",
            minOrders: "",
            maxOrders: "",
            chainzFee: "",
            shippingMultiplier: ""
        });
    };

    const handleCloseCreateModal = () => {
        if (!creating) {
            setCreateModalOpen(false);
            setFormData({
                name: "",
                minOrders: "",
                maxOrders: "",
                chainzFee: "",
                shippingMultiplier: ""
            });
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleEditInputChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCreateTier = () => {
        if (!formData.name || !formData.minOrders || !formData.maxOrders || !formData.chainzFee || !formData.shippingMultiplier) {
            notification.error({ message: "Please fill all fields" });
            return;
        }

        if (parseFloat(formData.minOrders) >= parseFloat(formData.maxOrders)) {
            notification.error({ message: "Min orders must be less than max orders" });
            return;
        }

        setCreating(true);
        addServiceFeeTier({
            name: formData.name,
            minOrders: parseFloat(formData.minOrders),
            maxOrders: parseFloat(formData.maxOrders),
            chainzFee: parseFloat(formData.chainzFee),
            shippingMultiplier: parseFloat(formData.shippingMultiplier)
        }, undefined);
        // Close modal and refresh data
        setCreating(false);
        setCreateModalOpen(false);
        getServiceFeeTiers();
        loadOs();
        setFormData({
            name: "",
            minOrders: "",
            maxOrders: "",
            chainzFee: "",
            shippingMultiplier: ""
        });
    };

    const handleOpenEditModal = (tier) => {
        setEditingTier(tier);
        setEditFormData({
            name: tier.name || "",
            minOrders: tier.minOrders?.toString() || "",
            maxOrders: tier.maxOrders?.toString() || "",
            chainzFee: tier.chainzFee?.toString() || "",
            shippingMultiplier: tier.shippingMultiplier?.toString() || ""
        });
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        if (!editing) {
            setEditModalOpen(false);
            setEditingTier(null);
            setEditFormData({
                name: "",
                minOrders: "",
                maxOrders: "",
                chainzFee: "",
                shippingMultiplier: ""
            });
        }
    };

    const handleOpenDeleteConfirm = (tier) => {
        setTierToDelete(tier);
        setDeleteConfirmOpen(true);
    };

    const handleCloseDeleteConfirm = () => {
        if (!deleting) {
            setDeleteConfirmOpen(false);
            setTierToDelete(null);
        }
    };

    const handleUpdateTier = () => {
        if (!editFormData.name || !editFormData.minOrders || !editFormData.maxOrders || !editFormData.chainzFee || !editFormData.shippingMultiplier) {
            notification.error({ message: "Please fill all fields" });
            return;
        }

        if (parseFloat(editFormData.minOrders) >= parseFloat(editFormData.maxOrders)) {
            notification.error({ message: "Min orders must be less than max orders" });
            return;
        }

        if (!editingTier?._id) {
            notification.error({ message: "Invalid tier selected" });
            return;
        }

        setEditing(true);
        editServiceFeeTier({
            tierId: editingTier._id,
            name: editFormData.name,
            minOrders: parseFloat(editFormData.minOrders),
            maxOrders: parseFloat(editFormData.maxOrders),
            chainzFee: parseFloat(editFormData.chainzFee),
            shippingMultiplier: parseFloat(editFormData.shippingMultiplier)
        })
            .then(() => {
                setEditModalOpen(false);
                setEditingTier(null);
                setEditFormData({
                    name: "",
                    minOrders: "",
                    maxOrders: "",
                    chainzFee: "",
                    shippingMultiplier: ""
                });
                getServiceFeeTiers();
                loadOs();
            })
            .finally(() => {
                setEditing(false);
            });
    };

    const handleDeleteTier = () => {
        if (!tierToDelete?._id) {
            notification.error({ message: "Invalid tier selected" });
            return;
        }

        setDeleting(true);
        deleteServiceFeeTier({ tierId: tierToDelete._id })
            .then(() => {
                setDeleteConfirmOpen(false);
                setTierToDelete(null);
                getServiceFeeTiers();
                loadOs();
            })
            .finally(() => {
                setDeleting(false);
            });
    };

    const osById = Object.fromEntries((osTiers || []).map((tier) => [tier._id, tier]));
    const tiersData = tiers?.map((tier) => ({
        _id: tier._id,
        name: tier.name,
        minOrders: tier.minOrders,
        maxOrders: tier.maxOrders,
        chainzFee: tier.chainzFee,
        shippingMultiplier: tier.shippingMultiplier,
        ...(osById[tier._id] || {}),
    })) || [];
    const archivedTiers = (osTiers || []).filter((tier) => tier.archived);

    const runTierAction = (action, tierId) => {
        const call = action === "duplicate" ? os.duplicateTier : action === "archive" ? os.archiveTier : os.restoreTier;
        call(token, tierId)
            .then(() => {
                notification.success({ message: action === "duplicate" ? "Tier duplicated." : action === "archive" ? "Tier archived." : "Tier restored." });
                getServiceFeeTiers();
                loadOs();
            })
            .catch(() => {});
    };

    const openHistory = (tier) => {
        setHistory(tier);
        os.tierHistory(token, tier._id)
            .then((data) => setHistoryRows(data?.history || data?.docs || (Array.isArray(data) ? data : [])))
            .catch(() => setHistoryRows([]));
    };

    React.useEffect(() => {
        const open = () => handleOpenCreateModal();
        window.addEventListener("fo-new-tier", open);
        return () => window.removeEventListener("fo-new-tier", open);
    }, []);

    return (
        <div className="fo-stack-sm">
            {series.length > 0 && (
                <section className="fo-card">
                    <div className="fo-card-head"><h2>Revenue by tier</h2></div>
                    <FoTable
                        pagination={false}
                        rowKey="month"
                        dataSource={series}
                        empty="No tier revenue yet."
                        columns={[
                            { title: "Month", dataIndex: "label" },
                            {
                                title: "Tiers",
                                dataIndex: "tiers",
                                render: (items) => (items || []).map((item) => `${item.name} ${formatEgp(item.revenue)}`).join(" · ") || "—",
                            },
                        ]}
                    />
                </section>
            )}
            {isLoading && <div className="fo-empty">Loading tiers…</div>}
            {!isLoading && tiersData.length === 0 && <div className="fo-empty">No tiers yet.</div>}
            {tiersData.map((tier) => (
                <article className="fo-tier" key={tier._id}>
                    <div className="fo-tier-id">{String(tier.name || "T").slice(0, 2).toUpperCase()}</div>
                    <div className="fo-tier-body">
                        <div className="fo-tier-name">
                            {tier.name}
                            <span className="fo-badge fo-badge-g"><i />Active</span>
                        </div>
                        <div className="fo-tier-range">
                            {tier.minOrders}–{tier.maxOrders ?? "—"} orders / month · {tier.chainzFee} EGP / order · {tier.shippingMultiplier}x shipping
                        </div>
                        <div className="fo-tier-stats">
                            <div><b>{tier.brandCount ?? "—"}</b><span>Brands</span></div>
                            <div><b>{tier.ordersPerMonth ?? "—"}</b><span>Orders / month</span></div>
                            <div><b>{tier.revenuePerMonth == null ? "—" : `${formatEgp(tier.revenuePerMonth)} EGP`}</b><span>Revenue / month</span></div>
                            <div><b>{tier.averagePerBrand == null ? "—" : `${formatEgp(tier.averagePerBrand)} EGP`}</b><span>Average / brand</span></div>
                        </div>
                        {tier.brands?.length > 0 && (
                            <div className="fo-tier-range">{tier.brands.map((brand) => brand.brandName).join(", ")}</div>
                        )}
                    </div>
                    <div className="fo-tier-acts">
                        <button type="button" className="fo-btn" onClick={() => handleOpenEditModal(tier)}>Edit</button>
                        <button type="button" className="fo-btn" onClick={() => runTierAction("duplicate", tier._id)}>Duplicate</button>
                        <button type="button" className="fo-btn" onClick={() => openHistory(tier)}>History</button>
                        <button type="button" className="fo-btn" onClick={() => runTierAction("archive", tier._id)}>Archive</button>
                        <button type="button" className="fo-btn" onClick={() => handleOpenDeleteConfirm(tier)}>Delete</button>
                    </div>
                </article>
            ))}

            {archivedTiers.length > 0 && (
                <section className="fo-card">
                    <div className="fo-card-head"><h2>Archived</h2></div>
                    {archivedTiers.map((tier) => (
                        <div className="fo-line" key={tier._id}>
                            <span>{tier.name}</span>
                            <button type="button" className="fo-btn" onClick={() => runTierAction("restore", tier._id)}>Restore</button>
                        </div>
                    ))}
                </section>
            )}
            <FoDialog open={Boolean(history)} title={history ? `${history.name} history` : ""} onClose={() => setHistory(null)} wide>
                <FoTable
                    pagination={false}
                    rowKey={(row, index) => `${row.action}-${row.createdAt}-${index}`}
                    dataSource={historyRows}
                    empty="No history yet."
                    columns={[
                        { title: "When", dataIndex: "createdAt", render: (value) => formatStamp(value) },
                        { title: "Action", dataIndex: "action" },
                        { title: "Actor", dataIndex: "actor" },
                        { title: "Note", dataIndex: "note" },
                        { title: "Previous", dataIndex: "previousValue", render: (value) => describeValue(value) },
                        { title: "Next", dataIndex: "nextValue", render: (value) => describeValue(value) },
                    ]}
                />
            </FoDialog>

            {/* Create Tier Modal */}
            <Modal
                open={createModalOpen}
                onClose={handleCloseCreateModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Fade in={createModalOpen}>
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 500 },
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            outline: 'none',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "var(--secColor)" }}>
                            Create New Tier
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Tier Name *
                            </Typography>
                            <MainInput
                                fullWidth
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Enter tier name"
                                disabled={creating}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Min Orders *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                value={formData.minOrders}
                                onChange={(e) => handleInputChange('minOrders', e.target.value)}
                                placeholder="Enter minimum orders"
                                disabled={creating}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Max Orders *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                value={formData.maxOrders}
                                onChange={(e) => handleInputChange('maxOrders', e.target.value)}
                                placeholder="Enter maximum orders"
                                disabled={creating}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Chainz Fee (EGP) *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                step="0.01"
                                value={formData.chainzFee}
                                onChange={(e) => handleInputChange('chainzFee', e.target.value)}
                                placeholder="Enter chainz fee"
                                disabled={creating}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Shipping Multiplier *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                step="0.01"
                                value={formData.shippingMultiplier}
                                onChange={(e) => handleInputChange('shippingMultiplier', e.target.value)}
                                placeholder="Enter shipping multiplier"
                                disabled={creating}
                            />
                        </Box>

                        {creating && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <CircularProgress size={24} sx={{ mr: 2, color: 'var(--secColor)' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Creating...
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                            <MUIButton
                                onClick={handleCloseCreateModal}
                                disabled={creating}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </MUIButton>
                            <MUIButton
                                onClick={handleCreateTier}
                                disabled={creating}
                                sx={{ ...main_button, textTransform: 'none' }}
                            >
                                {creating ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Create Tier'}
                            </MUIButton>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>

            {/* Edit Tier Modal */}
            <Modal
                open={editModalOpen}
                onClose={handleCloseEditModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Fade in={editModalOpen}>
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 500 },
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            outline: 'none',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: "var(--secColor)" }}>
                            Edit Tier
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Tier Name *
                            </Typography>
                            <MainInput
                                fullWidth
                                value={editFormData.name}
                                onChange={(e) => handleEditInputChange('name', e.target.value)}
                                placeholder="Enter tier name"
                                disabled={editing}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Min Orders *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                value={editFormData.minOrders}
                                onChange={(e) => handleEditInputChange('minOrders', e.target.value)}
                                placeholder="Enter minimum orders"
                                disabled={editing}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Max Orders *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                value={editFormData.maxOrders}
                                onChange={(e) => handleEditInputChange('maxOrders', e.target.value)}
                                placeholder="Enter maximum orders"
                                disabled={editing}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Chainz Fee (EGP) *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                step="0.01"
                                value={editFormData.chainzFee}
                                onChange={(e) => handleEditInputChange('chainzFee', e.target.value)}
                                placeholder="Enter chainz fee"
                                disabled={editing}
                            />
                        </Box>

                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Shipping Multiplier *
                            </Typography>
                            <MainInput
                                fullWidth
                                type="number"
                                step="0.01"
                                value={editFormData.shippingMultiplier}
                                onChange={(e) => handleEditInputChange('shippingMultiplier', e.target.value)}
                                placeholder="Enter shipping multiplier"
                                disabled={editing}
                            />
                        </Box>

                        {editing && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <CircularProgress size={24} sx={{ mr: 2, color: 'var(--secColor)' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Updating...
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                            <MUIButton
                                onClick={handleCloseEditModal}
                                disabled={editing}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </MUIButton>
                            <MUIButton
                                onClick={handleUpdateTier}
                                disabled={editing}
                                sx={{ ...main_button, textTransform: 'none' }}
                            >
                                {editing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                            </MUIButton>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>

            {/* Delete Tier Confirmation Modal */}
            <Modal
                open={deleteConfirmOpen}
                onClose={handleCloseDeleteConfirm}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Fade in={deleteConfirmOpen}>
                    <Paper
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: { xs: '90%', sm: 400 },
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2,
                            outline: 'none',
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: "error.main" }}>
                            Delete Tier
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                            Are you sure you want to delete this tier? This action cannot be undone.
                        </Typography>
                        {deleting && (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                <CircularProgress size={24} sx={{ mr: 2, color: 'error.main' }} />
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Deleting...
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                            <MUIButton
                                onClick={handleCloseDeleteConfirm}
                                disabled={deleting}
                                sx={{ textTransform: 'none' }}
                            >
                                Cancel
                            </MUIButton>
                            <MUIButton
                                onClick={handleDeleteTier}
                                disabled={deleting}
                                sx={{
                                    ...main_button,
                                    bgcolor: 'error.main',
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: 'error.dark',
                                    },
                                }}
                            >
                                {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Delete'}
                            </MUIButton>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>
        </div>
    );
};

const mapStateToProps = (state) => ({
    isLoading: state?.records?.isLoading || state?.wait?.isLoading,
    tiers: state?.records?.tiers,
    vendors: state?.records?.vendors
});

const mapDispatchToProps = { getServiceFeeTiers, addServiceFeeTier, getVendors, editServiceFeeTier, deleteServiceFeeTier };

export default connect(mapStateToProps, mapDispatchToProps)(ServiceFeeTiers);
